import moment from 'moment';

import KlassScopePermission from '@/utils/permissions/KlassScopePermission';
import Permission, { PermissionParams } from '@/utils/permissions/Permission';
import Enrollment, { EnrollmentStatusEnum } from '@/models/Enrollment';
import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import ScheduledLessonReport, {
  ActiveEnum,
  EnrollmentAndReportStatus,
  ScheduledLessonReportEnum,
  TranferredEnum,
} from '@/models/ScheduledLessonReport';
import { studentReportStatus } from '@/utils/studentReportStatus';

const EDITABLE_STATUS: EnrollmentAndReportStatus[] = [
  EnrollmentStatusEnum.ACTIVE,
  ActiveEnum.CANCELED_IN_FUTURE,
  ActiveEnum.TRANSFERED_IN_FUTURE,
];

interface StudentReportPermissionParams
  extends PermissionParams<ScheduledLessonReport> {
  klass?: Klass;
  enrollment?: Enrollment;
  scheduledLesson?: ScheduledLesson;
}

export default class StudentReportPermission extends Permission<ScheduledLessonReport> {
  klass?: Klass;
  enrollment?: Enrollment;
  scheduledLesson?: ScheduledLesson;
  klassScope: KlassScopePermission;

  constructor({
    user,
    model,
    klass,
    enrollment,
    scheduledLesson,
  }: StudentReportPermissionParams) {
    super({ user, model });
    this.klass = klass;
    this.enrollment = enrollment;
    this.scheduledLesson = scheduledLesson;
    this.klassScope = new KlassScopePermission({
      user,
      klass,
      scheduledLesson,
    });
  }

  isFromUnitKlass(): boolean {
    return this.klassScope.isFromUnitKlass();
  }

  isTeacherInKlass(): boolean {
    return this.klassScope.isTeacherInKlass();
  }

  wasUpdatedAfterLesson(): boolean {
    if (!this.enrollment || !this.scheduledLesson) return false;
    return moment(this.scheduledLesson.datetime).isBefore(
      this.enrollment.updatedAt,
    );
  }

  reportStatus(): EnrollmentAndReportStatus | undefined {
    if (!this.enrollment) return undefined;
    return studentReportStatus({
      enrollmentStatus: this.enrollment.status,
      reportStatus: this.model?.status,
      wasUpdatedAfterLesson: this.wasUpdatedAfterLesson(),
    });
  }

  canManage(): boolean {
    if (!this.user) return false;
    if (!this.hasTeacherPrivileges()) return false;
    if (this.hasSuperPrivileges()) return true;
    if (this.hasUnitPrivileges()) return this.isFromUnitKlass();
    return this.isFromUnitKlass() && this.isTeacherInKlass();
  }

  canEditStudentReport(): boolean {
    if (!this.canManage()) return false;
    if (this.enrollment?.status === EnrollmentStatusEnum.AWAITING_PAYMENT) {
      return false;
    }
    if (this.hasSuperPrivileges()) return true;
    const status = this.reportStatus();
    return !!status && EDITABLE_STATUS.includes(status);
  }

  canEditReportPanel(): boolean {
    if (!this.canManage()) return false;
    if (!this.model) return true;
    return this.model.status === ScheduledLessonReportEnum.ACTIVE;
  }

  canActivateStudentReport(): boolean {
    return this.reportStatus() === TranferredEnum.TRANSFERRED_TO_CLASS;
  }

  isStudentFromAnotherUnit(): boolean {
    if (!this.klass || !this.enrollment) return false;
    return !this.enrollment.student.unitsIds.includes(this.klass.unitId);
  }

  canCreateOccurrence(): boolean {
    return this.canManage();
  }

  isWithinLatenessWindow(): boolean {
    if (!this.scheduledLesson || !this.klass) return false;
    const lessonStart = moment(this.scheduledLesson.datetime);
    const notifyLimit = lessonStart
      .clone()
      .add(this.klass.duration / 2, 'minutes');
    const now = moment();
    return now.isSameOrAfter(lessonStart) && now.isBefore(notifyLimit);
  }

  canNotifyLateness(): boolean {
    if (!this.enrollment) return false;
    if (!this.canManage()) return false;
    if (this.enrollment.status !== EnrollmentStatusEnum.ACTIVE) return false;
    if (this.model?.presence) return false;
    return this.isWithinLatenessWindow();
  }

  canNotifyStudentPresence(): boolean {
    if (!this.enrollment || this.model) return false;
    if (!this.canCreateOccurrence()) return false;
    if (this.enrollment.status !== EnrollmentStatusEnum.AWAITING_PAYMENT) {
      return false;
    }
    return this.isStudentFromAnotherUnit();
  }
}
